interface TickGroupRule {
  originalStr: string;
  type: "cabinet_range" | "range" | "cabinet_value";
  min?: number;
  max?: number;
  tick?: number;
  value?: number; // For cabinet_value type
}

interface TickGroupInfo {
  minimumValue: number | null;
  maximumValue: number | null;
  tickSize: number | null;
  cabinetError: boolean;
  cabinetIndicator: boolean;
  decimalPlaces: number | null;
}

export function parseTickGroupString(
  price: number,
  tickGroupStr: string | undefined,
): TickGroupInfo {
  tickGroupStr = tickGroupStr ? tickGroupStr.trim() : "";

  const decimalPlacesScientificNotation = (n: number): number => {
    if (!isFinite(n) || Number.isInteger(n)) {
      return 0;
    }
    let numStr = String(n);
    // Check for scientific notation (e.g., "5e-7", "1.23e-4")
    const lowerNumStr = numStr.toLowerCase();
    if (lowerNumStr.includes("e-")) {
      const parts = lowerNumStr.split("e-");
      const coefficient = parts[0];
      const exponent = parseInt(parts[1], 10);

      let decimalPlaces = exponent;
      if (coefficient.includes(".")) {
        const coefficientDecimalPart = coefficient.split(".")[1];
        decimalPlaces += coefficientDecimalPart.length;
      }
      return decimalPlaces;
    }

    if (numStr.includes(".")) {
      return numStr.split(".")[1].length;
    }
    return 0;
  };

  let maxDecimalPlaces: number | null = null;

  // 1. Handle GENERAL presets
  if (tickGroupStr.toLowerCase().startsWith("general")) {
    const generalTick = parseFloat(tickGroupStr.substring("GENERAL".length));
    if (!isNaN(generalTick)) {
      maxDecimalPlaces = decimalPlacesScientificNotation(generalTick);
      return {
        minimumValue: -1000000,
        maximumValue: 1000000,
        tickSize: generalTick,
        cabinetError: false,
        cabinetIndicator: false, // Cabinet not indicated for general preset
        decimalPlaces: maxDecimalPlaces,
      };
    }
  }
  let cabinetIndicator: boolean = false;

  const rules: TickGroupRule[] = [];
  const parts = tickGroupStr.split(",");

  for (const part of parts) {
    let currentPart = part.trim();
    let isCabinetRule = false;
    let cabinetType = ""; // 'C' or 'VC'

    if (currentPart.endsWith("VC")) {
      isCabinetRule = true;
      cabinetType = "VC";
      currentPart = currentPart.slice(0, -2);
    } else if (currentPart.endsWith("C")) {
      isCabinetRule = true;
      cabinetType = "C";
      currentPart = currentPart.slice(0, -1);
    }

    // Try to match min-max@tick format
    const rangeMatch = currentPart.match(/^(-?[\d.]+)-(-?[\d.]+)@([\d.]+)$/);
    if (rangeMatch) {
      const tickValue = parseFloat(rangeMatch[3]);
      const currentDecimals = decimalPlacesScientificNotation(tickValue);
      if (maxDecimalPlaces === null || currentDecimals > maxDecimalPlaces) {
        maxDecimalPlaces = currentDecimals;
      }
      rules.push({
        originalStr: part.trim(),
        type: isCabinetRule ? "cabinet_range" : "range",
        min: parseFloat(rangeMatch[1]),
        max: parseFloat(rangeMatch[2]),
        tick: tickValue,
      });
      continue;
    }

    // Try to match value format (only if it was marked as cabinet)
    if (isCabinetRule) {
      cabinetIndicator = true;
      const valueMatch = currentPart.match(/^(-?[\d.]+)$/);
      if (valueMatch) {
        const cabinetValue = parseFloat(valueMatch[1]);
        const currentDecimals = decimalPlacesScientificNotation(cabinetValue);
        // if (maxDecimalPlaces === null || currentDecimals > maxDecimalPlaces) {
        //   maxDecimalPlaces = currentDecimals;
        // }
        rules.push({
          originalStr: part.trim(),
          type: "cabinet_value",
          value: cabinetValue,
        });
        continue;
      }
    }
  }

  let outMinimumValue: number | null = null;
  let outMaximumValue: number | null = null;
  let outTickSize: number | null = null;
  let outCabinetPrice: boolean = false;

  // Calculate overall min/max from range-defining rules
  const rangeDefiningRules = rules.filter(
    (r) => r.type === "range" || r.type === "cabinet_range",
  );
  if (rangeDefiningRules.length > 0) {
    outMinimumValue = Math.min(...rangeDefiningRules.map((r) => r.min!));
    outMaximumValue = Math.max(...rangeDefiningRules.map((r) => r.max!));
  }

  let tickSetByExplicitRange = false;

  // Priority 1: Explicit Cabinet Ranges
  for (const rule of rules) {
    if (
      rule.type === "cabinet_range" &&
      price >= rule.min! &&
      price <= rule.max!
    ) {
      outCabinetPrice = true;
      outTickSize = rule.tick!;
      cabinetIndicator = true;
      tickSetByExplicitRange = true;
      break;
    }
  }

  // Priority 2: Explicit Normal Ranges
  if (!tickSetByExplicitRange) {
    for (const rule of rules) {
      if (rule.type === "range" && price >= rule.min! && price <= rule.max!) {
        // outCabinetPrice remains false (unless a cabinet_range also matched, which is unlikely here)
        outTickSize = rule.tick!;
        tickSetByExplicitRange = true;
        break;
      }
    }
  }

  // Priority 3: Explicit Cabinet Values (price <= rule.value)
  // This now only affects outCabinetPrice, not outTickSize.
  if (!outCabinetPrice) {
    // Check only if not already set by a cabinet_range
    let bestFitCabinetValueRule: TickGroupRule | null = null;
    for (const rule of rules) {
      if (rule.type === "cabinet_value" && price <= rule.value!) {
        if (
          !bestFitCabinetValueRule ||
          rule.value! < bestFitCabinetValueRule.value!
        ) {
          bestFitCabinetValueRule = rule;
        }
      }
    }
    if (bestFitCabinetValueRule) {
      outCabinetPrice = true;
      cabinetIndicator = true;
    }
  }

  // Priority 4: Implicit Cabinet Zone (price is below any normal range, and a cabinet marker exists)
  if (!outCabinetPrice) {
    // Check only if not already set by cabinet_range or explicit cabinet_value
    const normalRanges = rules.filter((r) => r.type === "range");
    const cabinetValueRules = rules.filter((r) => r.type === "cabinet_value");

    if (normalRanges.length > 0 && cabinetValueRules.length > 0) {
      const minNormalRangeStart = Math.min(...normalRanges.map((r) => r.min!));
      if (price < minNormalRangeStart) {
        outCabinetPrice = true;
        cabinetIndicator = true;
        // DO NOT set outTickSize based on cabinet_value rules here.
        // outTickSize should have been determined by the logic above (either from an
        // explicit range or defaulted from a normal range).
      }
    }
  }
  return {
    minimumValue: outMinimumValue,
    maximumValue: outMaximumValue,
    tickSize: outTickSize,
    cabinetError: outCabinetPrice,
    cabinetIndicator: cabinetIndicator,
    decimalPlaces: maxDecimalPlaces,
  };
}
